import { makeStyles } from "tss-react/mui"
import { Box, Drawer, IconButton, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCartShopping, faXmark } from "@fortawesome/pro-solid-svg-icons"

import { useMoney } from "../../../../../../modules/currency/useMoney"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { StandardLoadingButton } from "../../../../buttons/LoadingButton"
import { StandardAlertInfo } from "../../../../alert/StandardAlertInfo"
import { IconBadge } from "../../../../badges/IconBadge"

import type { TakeawayCartItem, TakeawayKitchen, TakeawayProduct } from "../takeawayTypes"
import { calcItemsTotal, isValidApiString } from "./takeawayCheckoutLib"
import { TakeawayBasketDateCard } from "./TakeawayBasketDateCard"

const useStyles = makeStyles()((theme) => ({
    drawerRoot: {
        zIndex: 1400,
    },
    drawer: {
        width: "27.5rem",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
    },
    header: {
        display: "flex",
        alignItems: "center",
        padding: "1rem",
        gap: ".75rem",
        flexShrink: 0,
        background: theme.palette.common.white,
    },
    headerTitle: {
        flex: 1,
        color: theme.palette.text.primary,
    },
    closeButton: {
        color: theme.palette.tailwind.slate[500],
        transition: "all ease-in-out 0.3s",
        padding: 0,
        "&:hover": {
            background: "none",
            color: theme.palette.text.primary,
        },
    },
    openingHoursBanner: {
        flexShrink: 0,
    },
    scrollContent: {
        flex: 1,
        overflowY: "auto",
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    footer: {
        padding: "1rem",
        flexShrink: 0,
    },
    confirmButton: {
        justifyContent: "space-between",
    },
}))

type TakeawayBasketDrawerContentProps = Readonly<{
    open: boolean
    sortedDates: string[]
    cartByDate: Record<string, TakeawayCartItem[]>
    kitchen: TakeawayKitchen
    moneyFactory: ReturnType<typeof useMoney>
    totalPrice: ReturnType<typeof calcItemsTotal>
    isCheckoutOpen: boolean
    canConfirm: boolean
    onQuantityChange: (product: TakeawayProduct, quantity: number, dateKey?: string) => void
    onClose: () => void
    onConfirm: () => void
}>

export function TakeawayBasketDrawerContent({
    open,
    sortedDates,
    cartByDate,
    kitchen,
    moneyFactory,
    totalPrice,
    isCheckoutOpen,
    canConfirm,
    onQuantityChange,
    onClose,
    onConfirm,
}: TakeawayBasketDrawerContentProps) {
    const { classes } = useStyles()
    const openingHours = isValidApiString(kitchen.brandingDetails?.openingHours)
        ? kitchen.brandingDetails!.openingHours
        : undefined

    return (
        <Drawer
            anchor="right"
            variant="temporary"
            open={open && !isCheckoutOpen}
            onClose={onClose}
            className={classes.drawerRoot}
        >
            <Box className={classes.drawer}>
                <Box className={classes.header}>
                    <IconBadge variant="font-awesome" color="blue" fontAwesomeIcon={faCartShopping} />
                    <LocalizedStrict id="takeaway-checkout-order-summary-title">
                        <Typography variant="h6" className={classes.headerTitle}>
                            Order summary
                        </Typography>
                    </LocalizedStrict>
                    <IconButton className={classes.closeButton} onClick={onClose}>
                        <FontAwesomeIcon icon={faXmark} fontSize={20} />
                    </IconButton>
                </Box>

                <Box className={classes.scrollContent}>
                    {openingHours && (
                        <Box className={classes.openingHoursBanner}>
                            <LocalizedStrict
                                id="takeaway-checkout-opening-hours-banner"
                                vars={{ kitchenName: kitchen.name, hours: openingHours }}
                                attrs={{ title: true }}
                            >
                                <StandardAlertInfo title="" severity="success" />
                            </LocalizedStrict>
                        </Box>
                    )}

                    {sortedDates.map((dateKey) => (
                        <TakeawayBasketDateCard
                            key={dateKey}
                            dateKey={dateKey}
                            items={cartByDate[dateKey] ?? []}
                            moneyFactory={moneyFactory}
                            onQuantityChange={onQuantityChange}
                        />
                    ))}
                </Box>

                <Box className={classes.footer}>
                    <StandardLoadingButton
                        loading={false}
                        disabled={!canConfirm}
                        onClick={onConfirm}
                        color="black"
                        size="large"
                        className={classes.confirmButton}
                    >
                        <LocalizedStrict id="takeaway-checkout-confirm-button">
                            <Typography variant="inherit">Complete payment</Typography>
                        </LocalizedStrict>
                        {totalPrice && <Typography variant="inherit">{moneyFactory.format(totalPrice)}</Typography>}
                    </StandardLoadingButton>
                </Box>
            </Box>
        </Drawer>
    )
}
